import React, { useState, useEffect } from "react";
import Select from "react-select";
import { authHeaders, formatToZMW, rootUrl, sumOfArray } from "../../../helpers";
import { useOutletContext } from "react-router-dom";
import { useAuth } from "../../../context/auth-context";
import { FaList, FaSpinner, FaToggleOff, FaToggleOn, FaBuilding, FaFilter, FaEye, FaEyeSlash, FaCalendarAlt, FaMoneyBillWave, FaArrowUp, FaArrowDown } from "react-icons/fa";
import { BiCategory } from "react-icons/bi";

export default function Department({ startDate, endDate, triggerFetch }) {

      const [allCash, setAllCash] = useState([]);
      const [filteredData, setFilteredData] = useState([]);
      const [openingBalance, setOpeningBalance] = useState(0);
      const [fromDate, setFromDate] = useState("");
      const [toDate, setToDate] = useState("");
      const [selectedCOA, setSelectedCOA] = useState(null); 
      const [showDetailedView, setShowDetailedView] = useState(true);
      const [selectedDept, setSelectedDept] = useState(null);
      const [selectedProgram, setSelectedProgram] = useState(null);
    
      const [loading, setLoading] = useState(true)
    
      const {allData, setAllData} = useOutletContext();
      const auth = useAuth();

      const displayTable = (data, bf) => {
        let totalCredits = 0;
        let totalDebits = 0;
        let balance = bf;
      
        // Group data by dept
        const groupedData = data.reduce((acc, obj) => {
          const dept = allData.budget.find((a) => a.id === obj.coa_id)?.dept || "Unknown";
          if (!acc[dept]) acc[dept] = [];
          acc[dept].push(obj);
          return acc;
        }, {});
      
        const rows = [];

      
        Object.keys(groupedData).forEach((coa) => {
          let coaDebits = 0;
          let coaCredits = 0;
      
          // Calculate COA totals
          groupedData[coa].forEach((obj) => {
            const debit = obj.type === "income" ? parseFloat(obj.amount) : 0;
            const credit = obj.type === "expense" ? parseFloat(obj.amount) : 0;
            coaDebits += debit;
            coaCredits += credit;
            totalCredits += credit;
            totalDebits += debit;
            balance += debit - credit;
          });
      
          if (showDetailedView) {
            // Detailed transactions
            groupedData[coa].sort((a,b)=> b.coa_id - a.coa_id).forEach((obj) => {
              const debit = obj.type === "income" ? parseFloat(obj.amount) : 0;
              const credit = obj.type === "expense" ? parseFloat(obj.amount) : 0;
              const trans = allData.budget.find((a) => a.id === obj.coa_id)
              rows.push(
                <tr key={obj.id} className="text-left hover:bg-blue-50 transition-colors">
                  {/* Transaction details */}
                  <td className="px-4 py-3 border-b border-gray-200 text-left text-gray-700">
                    {allData.dept.find(a=>a.id === trans?.dept)?.name}
                  </td>
                  <td className="px-4 py-3 border-b border-gray-200 text-left text-gray-700">{allData.program.find(a=>a.programCode === trans?.program)?.name}</td>
                  <td className="px-4 py-3 border-b border-gray-200 text-left text-gray-700">{allData.subProgram.find(a=>a.programCode === trans?.program && a.subProgramCode === trans?.subprogram)?.name}</td>
                  <td className="px-4 py-3 border-b border-gray-200 text-left text-gray-700 font-mono text-sm">{trans?.coa}</td>
                  <td className="px-4 py-3 border-b border-gray-200 text-left text-gray-700">{obj?.voucher || '-'}</td>
                  <td className="px-4 py-3 border-b border-gray-200 text-left text-gray-700">
                    {allData?.suppliers?.find(a => a.id == obj?.paye)?.name || '-'}
                  </td>
                  <td className="px-4 py-3 border-b border-gray-200 text-right font-medium text-green-700">{formatToZMW(debit)}</td>
                  <td className="px-4 py-3 border-b border-gray-200 text-right font-medium text-red-700">{formatToZMW(credit)}</td>
                  <td className="px-4 py-3 border-b border-gray-200 text-right font-medium text-gray-800">{formatToZMW(balance)}</td>
                </tr>
              );
            });
      
            // Subtotal row for detailed view
            rows.push(
              <tr key={`subtotal-${coa}`} className="bg-gradient-to-r from-gray-100 to-gray-50 border-t-2 border-gray-300">
                <td className="px-4 py-3 font-bold text-left" colSpan="6">
                  <span className="inline-flex items-center gap-2">
                    <FaBuilding className="text-gray-600" />
                    Subtotal for {allData.dept.find(a=>a.id == coa)?.name || 'Unknown Department'}
                  </span>
                </td>
                <td className="px-4 py-3 font-bold text-right text-green-800">{formatToZMW(coaDebits)}</td>
                <td className="px-4 py-3 font-bold text-right text-red-800">{formatToZMW(coaCredits)}</td>
                <td className="px-4 py-3 font-bold text-right text-gray-800">{formatToZMW(balance)}</td>
              </tr>
            );
          } else {
            // Summary view row
            rows.push(
              <tr key={`summary-${coa}`} className="bg-gradient-to-r from-gray-100 to-gray-50 border-t-2 border-gray-300 hover:bg-gray-200 transition-colors">
                <td className="px-4 py-3 font-bold text-left" colSpan="6">
                  <span className="inline-flex items-center gap-2">
                    <FaBuilding className="text-gray-600" />
                    {allData.dept.find(a=>a.id == coa)?.name || 'Unknown Department'}
                  </span>
                </td>
                <td className="px-4 py-3 font-bold text-right text-green-800">{formatToZMW(coaDebits)}</td>
                <td className="px-4 py-3 font-bold text-right text-red-800">{formatToZMW(coaCredits)}</td>
                <td className="px-4 py-3 font-bold text-right text-gray-800">
                  {formatToZMW(coaDebits - coaCredits)}
                </td>
              </tr>
            );
          }
        });
      
        // Total row
        const totals = (
          <tr className="bg-gradient-to-r from-gray-700 to-gray-800 text-white font-bold border-t-2 border-gray-400">
            <th className="px-4 py-3 text-left" colSpan="6">
              <span className="inline-flex items-center gap-2">
                <FaMoneyBillWave />
                Total
              </span>
            </th>
            <th className="px-4 py-3 text-right text-lg text-green-300">{formatToZMW(totalDebits)}</th>
            <th className="px-4 py-3 text-right text-lg text-red-300">{formatToZMW(totalCredits)}</th>
            <th className="px-4 py-3 text-right text-lg">{formatToZMW(balance)}</th>
          </tr>
        );
      
        return (
          <>
            {rows}
            {totals}
          </>
        );
      };

      useEffect(() => {
        const fetchData = async () => {
          const response = await fetch(rootUrl(`/getCashByDate/${auth.user?.cid}~${startDate}~${endDate}`), {
            headers: authHeaders(),
          });
          const result = await response.json();
          const data = result.data || [];
    
          setAllCash(data.cash);
          const filtered = data.cash;
          setOpeningBalance(data.openingBalance);
          setFilteredData(filtered);
          setLoading(false)
        };
    
        if(triggerFetch && startDate && endDate){
          setLoading(true)
          fetchData();
        }
      }, [triggerFetch]);

      // Filtering logic
      const handleFilter = () => {
        let filtered = allCash;
        if (selectedDept && selectedDept.value) {
          // Find all budget entries with the selected department
          const matchingBudget = allData.budget.filter(a => a.dept === selectedDept.value);
          const matchingIds = matchingBudget.map(a => a.id);
          filtered = filtered.filter((item) => matchingIds.includes(item.coa_id));
        }
        if (selectedProgram && selectedProgram.value) {
          // Find all budget entries with the selected program
          const matchingBudget = allData.budget.filter(a => a.program === selectedProgram.value);
          const matchingIds = matchingBudget.map(a => a.id);
          filtered = filtered.filter((item) => matchingIds.includes(item.coa_id));
        }
        setFilteredData(filtered);
      };

      const deptOptions = allData.dept?.map(dept => ({ value: dept.id, label: dept.name })) || [];
      const programOptions = allData.program?.map(prog => ({ value: prog.programCode, label: prog.name })) || [];
      return (
        <div className="w-full space-y-6">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className=" px-6 py-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-950 rounded-lg p-2">
                    <BiCategory className="text-white text-xl" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold">Department Report</h4>
                    <p className="text-gray-600 text-sm">Financial transactions by department</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailedView(!showDetailedView)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    showDetailedView
                      ? 'bg-blue-950/80 text-white hover:bg-blue-950/90'
                      : 'bg-blue-950/70 text-white hover:bg-blue-950/80'
                  }`}
                  title={showDetailedView ? "Switch to Summary View" : "Switch to Detailed View"}
                >
                  {showDetailedView ? <FaEyeSlash /> : <FaEye />}
                  <span className="hidden md:inline">{showDetailedView ? "Summary" : "Detailed"}</span>
                </button>
              </div>
            </div>
    
            <div className="p-6 space-y-6">
              {/* Filters Section */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                  <FaFilter className="text-blue-950" />
                  <h3 className="font-semibold text-gray-800">Filters</h3>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <Select
                      options={deptOptions}
                      value={selectedDept}
                      onChange={setSelectedDept}
                      placeholder="Select Department"
                      isClearable
                      className="w-full"
                    />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <Select
                      options={programOptions}
                      value={selectedProgram}
                      onChange={setSelectedProgram}
                      placeholder="Select Program"
                      isClearable
                      className="w-full"
                    />
                  </div>
                  <button 
                    onClick={handleFilter} 
                    className="bg-blue-950 hover:bg-blue-900 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
                  >
                    <FaFilter />
                    Filter
                  </button>
                </div>
              </div>
    
              {
                loading?
                <div className='flex flex-col justify-center items-center h-64 w-full bg-white rounded-2xl border border-gray-100 shadow-sm'>
                  <FaSpinner size={50} className="animate-spin text-blue-950 mb-4" />
                  <p className="text-gray-600 text-lg">Loading department data...</p>
                </div>
                :
                <>
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="table-auto w-full">
                      <thead>
                        <tr className="bg-gradient-to-r from-blue-950 to-blue-800 text-white">
                          <th className="px-4 py-3 text-left font-semibold border-r border-blue-700">Department</th>
                          <th className="px-4 py-3 text-left font-semibold border-r border-blue-700">Program</th>
                          <th className="px-4 py-3 text-left font-semibold border-r border-blue-700">Subprogram</th>
                          <th className="px-4 py-3 text-left font-semibold border-r border-blue-700">COA</th>
                          <th className="px-4 py-3 text-left font-semibold border-r border-blue-700">Voucher</th>
                          <th className="px-4 py-3 text-left font-semibold border-r border-blue-700">Payee Name</th>
                          <th className="px-4 py-3 text-right font-semibold border-r border-blue-700">Debit</th>
                          <th className="px-4 py-3 text-right font-semibold border-r border-blue-700">Credit</th>
                          <th className="px-4 py-3 text-right font-semibold">Balance</th>
                        </tr>
                      </thead>
                      <tbody id="trows">{displayTable(filteredData, openingBalance)}</tbody>
                    </table>
                  </div>
                </>
              }
            </div>
          </div>
        </div>
      );
}
